import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { RootState } from '../../../../store/root-reducer';
import {
  GetTaskerParams,
  GetTasksParams,
  UpdateTask,
  GetShareableTaskerParams,
} from '../../utils/types';
import {
  getShareableTaskerDataRequest,
  getTaskerDataRequest,
  getTaskStatusCountRequest,
  getWhatsappLinkRequest,
  updateTasksRequest,
} from '../../extra-actions';
import {
  onOutcomeChangeRequest,
  uploadAttachmentRequest,
  deleteAttachmentRequest,
} from '../../../sequence/extra-actions';
import {
  resetUploadAttachmentRequest,
  resetDeleteAttachmentRequest,
} from '../../../sequence/sequence-slice';
import TaskerModal from './tasker-modal';
import { TaskAction, TaskStatusKey } from '../../utils/enums';
import { SequenceStepType } from '../../../sequence/enums';
import {
  resetShareableTaskerState,
  resetTaskerState,
  insertTaskIntoTaskerData,
  resetGetWhatsappLinkState,
} from '../../tasks-slice';
import {
  updatePrimaryNumberRequest,
  createProspectNotesListRequest,
  updateProspectNoteRequest,
} from '../../../prospect/extra-actions';
import {
  resetUpdatePrimaryNumberState,
  resetCreateProspectNotesListRequest,
  resetUpdateProspectNoteRequest,
} from '../../../prospect/prospect-slice';

const mapStateToProps = (state: RootState) => ({
  getTaskerDataRequestStatus: state.tasks.getTaskerDataRequest.status,
  getShareableTaskerDataRequestStatus:
    state.tasks.getShareableTaskerDataRequest.status,
  taskActionRequestStatus: state.tasks.updateTasksRequest.status,
  taskerData: state.tasks.taskerData,
  allOutcomes: state.customOutcome.outcomes,
  outcomeChangeRequestStatus: state.sequence.onOutcomeChangeRequest.status,
  updateTaskNoteRequestStatus: state.tasks.updateTaskNoteRequest.status,
  shareableTaskerData: state?.tasks?.shareableTaskerData,
  getUndoTasksRequestStatus: state.tasks.getUndoTasksRequest.status,
  updatePrimaryNumberRequestStatus:
    state.prospect.updatePrimaryNumberRequest.status,
  updatePrimaryNumberResponse: state.prospect.updatePrimaryNumberResponse,
  getWhatsappLinkRequestStatus: state.tasks.getWhatsappLinkRequest.status,
  getWhatsappLinkRequestMessage: state.tasks.getWhatsappLinkRequest.message,
  getWhatsappLinkRequestError: state.tasks.getWhatsappLinkRequest.error,
  getWhatsappLinkResponse: state.tasks.getWhatsappLinkResponse,
  uploadAttachmentRequestStatus:
    state.sequence?.uploadAttachmentRequest?.status,
  uploadAttachmentRequestError: state.sequence?.uploadAttachmentRequest?.error,
  uploadedAttachment: state.sequence?.uploadedAttachment,
  deleteAttachmentRequestStatus:
    state.sequence?.deleteAttachmentRequest?.status,
  deleteAttachmentRequestError: state.sequence?.deleteAttachmentRequest?.error,
  deletedAttachmentId: state.sequence?.deletedAttachmentId,
  createProspectNoteRequestStatus:
    state.prospect.createProspectNotesListRequest.status,
  createProspectNoteRequestMessage:
    state.prospect.createProspectNotesListRequest.message,
  createProspectNoteResponse: state.prospect.createProspectNoteResponse,
  updateProspectNoteRequestStatus:
    state.prospect.updateProspectNoteRequest.status,
  updateProspectNoteRequestMessage:
    state.prospect.updateProspectNoteRequest.message,
});

const mapDispatchToProps = {
  updateTasksRequest: (payload: UpdateTask) => updateTasksRequest(payload),
  getTasksStatusCountRequest: (params: GetTasksParams) =>
    getTaskStatusCountRequest(params),
  getTaskerDataRequest: (params: GetTaskerParams) =>
    getTaskerDataRequest(params),
  resetTaskerState: () => resetTaskerState(),
  getShareableTaskerDataRequest: (hashId: GetShareableTaskerParams) =>
    getShareableTaskerDataRequest(hashId),
  resetShareableTaskerState: () => resetShareableTaskerState(),
  insertTaskIntoTaskerData: (payload: { task: any; index: number }) =>
    insertTaskIntoTaskerData(payload),
  onOutcomeChangeRequest: (
    sequenceId: number,
    prospectId: number,
    outcomeId: number,
  ) => onOutcomeChangeRequest({ sequenceId, prospectId, outcomeId }),
  updatePrimaryNumberRequest: (
    contactId: number,
    primaryPhoneProspectInfoId: number,
  ) => updatePrimaryNumberRequest({ contactId, primaryPhoneProspectInfoId }),
  resetUpdatePrimaryNumberState: () => resetUpdatePrimaryNumberState(),
  getWhatsappLinkRequest: (taskId: number) => getWhatsappLinkRequest(taskId),
  resetGetWhatsappLinkState: () => resetGetWhatsappLinkState(),
  sendUploadAttachmentRequest: (payload) => uploadAttachmentRequest(payload),
  sendDeleteAttachmentRequest: (payload) => deleteAttachmentRequest(payload),
  resetUploadAttachmentRequest: () => resetUploadAttachmentRequest(),
  resetDeleteAttachmentRequest: () => resetDeleteAttachmentRequest(),
  sendCreateProspectNoteRequest: (payload) =>
    createProspectNotesListRequest(payload),
  sendUpdateProspectNoteRequest: (payload) =>
    updateProspectNoteRequest(payload),
  resetCreateProspectNotesListRequest: () =>
    resetCreateProspectNotesListRequest(),
  resetUpdateProspectNoteRequest: () => resetUpdateProspectNoteRequest(),
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type IProps = ConnectedProps<typeof connector> &
  RouteComponentProps & {
    show: boolean;
    onHide: VoidFunction;
    taskStatus: TaskStatusKey;
    tasksCount: number;
    paginationOptions: { pageLimit: number; pageNumber: number };
    taskId: number;
    filters: GetTasksParams;
    onTaskNoteClick?: (tId: number, tNote: string) => void;
    onOutcomeChange: (outcome: { key: string; label: string }) => void;
    updateTasks: (
      tId: number,
      tAction: TaskAction,
      tType: SequenceStepType,
      extraPayload?: any,
    ) => void;
    resetTaskId: VoidFunction;
    onRefresh: VoidFunction;
    setShowGlobalRestrictionModal: (taskType: SequenceStepType) => void;
    shareableHashId?: string;
    setUserInteractedRef?: React.MutableRefObject<boolean>;
    sendUploadAttachmentRequest?: (payload: any) => void;
    sendDeleteAttachmentRequest?: (payload: any) => void;
    resetUploadAttachmentRequest?: () => void;
    resetDeleteAttachmentRequest?: () => void;
    uploadAttachmentRequestStatus?: any;
    uploadAttachmentRequestError?: any;
    uploadedAttachment?: any;
    deleteAttachmentRequestStatus?: any;
    deleteAttachmentRequestError?: any;
    deletedAttachmentId?: number;
    sendCreateProspectNoteRequest?: (payload: any) => void;
    sendUpdateProspectNoteRequest?: (payload: any) => void;
    resetCreateProspectNotesListRequest?: () => void;
    resetUpdateProspectNoteRequest?: () => void;
    createProspectNoteRequestStatus?: any;
    createProspectNoteRequestMessage?: string;
    createProspectNoteResponse?: any;
    updateProspectNoteRequestStatus?: any;
  };

export default withRouter(connector(TaskerModal));
